import { useState, useEffect } from "react";

export function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function formatMoney(value, currencyCode = "EUR") {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currencyCode || "EUR",
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatTime(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

const PAYMENT_METHOD_LABEL_KEYS = {
  CASH: "commerceCommon.paymentCash",
  CARD: "commerceCommon.paymentCard",
  BANK: "commerceCommon.paymentBankTransfer",
};

/**
 * The API returns payment method as a lowercase code (e.g. "cash"). Resolve it
 * through the same labels the payment picker uses so raw codes never reach the
 * user; unrecognized codes fall back to the raw value rather than hiding data.
 */
export function resolvePaymentMethodLabel(paymentMethod, t) {
  const key = PAYMENT_METHOD_LABEL_KEYS[String(paymentMethod || "").trim().toUpperCase()];
  return key ? t(key) : paymentMethod || "—";
}

/**
 * Available stock is measured per location, so a basket line may only be capped
 * while the location it was measured at is still the selected one. Returns null
 * when no trustworthy limit is known — callers then fall back to the scan and
 * sale-completion validations instead of guessing.
 */
export function resolveLineStockLimit(line, locationId) {
  if (!line || line.inventoryMode !== "aggregate") {
    return null;
  }

  if (String(line.availabilityLocationId ?? "") !== String(locationId ?? "")) {
    return null;
  }

  const raw = line.availableQuantity;
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }

  const limit = Number(raw);
  return Number.isFinite(limit) && limit >= 0 ? limit : null;
}

export function isLineAtStockLimit(line, locationId) {
  const limit = resolveLineStockLimit(line, locationId);
  return limit === null ? false : Number(line?.quantity || 0) >= limit;
}

/** True once the line carries availability read at the currently selected location. */
export function hasResolvedLineAvailability(line, locationId) {
  if (!line || line.inventoryMode !== "aggregate") {
    return true;
  }

  return resolveLineStockLimit(line, locationId) !== null;
}

/**
 * Single source of truth for "may this line go up by one?". Unresolved
 * availability (e.g. right after a location change) never allows an increment —
 * the cart must not guess while it is waiting for the new location's stock.
 */
export function canIncrementLine(line, locationId) {
  if (!line || line.inventoryMode !== "aggregate") {
    return false;
  }

  const limit = resolveLineStockLimit(line, locationId);
  return limit === null ? false : Number(line.quantity || 0) < limit;
}

/** Lines whose availability belongs to another location (or is missing). */
export function findStaleAvailabilityVariantIds(basket, locationId) {
  return (basket || [])
    .filter(
      (line) =>
        line?.inventoryMode === "aggregate" &&
        !hasResolvedLineAvailability(line, locationId)
    )
    .map((line) => line.variantId);
}

/** Shared Returns / Exchanges helpers for finding an original historical Sale. */
export function inferReturnableSaleLookupPayload(value) {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return null;
  }

  if (/^S-\d+$/i.test(normalized)) {
    return {
      saleNumber: normalized.toUpperCase(),
    };
  }

  return {
    inventoryUnitBarcode: normalized,
  };
}

export function isExactSaleNumberQuery(value) {
  return /^S-\d+$/i.test(String(value || "").trim());
}

export function shouldRunHistoricalSaleSearch(value) {
  const normalized = String(value || "").trim();
  if (normalized.length < 2) {
    return false;
  }

  // Exact sale numbers use the fast lookup path; keep barcode scans on submit.
  if (isExactSaleNumberQuery(normalized)) {
    return false;
  }

  return true;
}

export function formatSaleDateTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
  const time = formatTime(value);
  return time ? `${day} · ${time}` : day;
}

export function todayString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const normalizeDisplayToken = (value) => String(value || "").trim();

const collapseSlashSeparatedTitle = (value) => {
  const fallback = normalizeDisplayToken(value);
  if (!fallback) {
    return "";
  }

  return fallback
    .split(" / ")
    .map((segment) => {
      const colonIndex = segment.indexOf(":");
      return colonIndex === -1
        ? segment.trim()
        : segment.slice(colonIndex + 1).trim();
    })
    .filter(Boolean)
    .join(" ");
};

/**
 * Option dimension names come from the catalog, so the same shop can hold both
 * Albanian ("Ngjyra", "Madhësia") and English ("Color", "Size") names, with or
 * without diacritics. Strip accents before matching so detection never depends
 * on how a particular product was typed in.
 */
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");

const normalizeOptionName = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");

const COLOR_OPTION_NAMES = new Set(["color", "colour", "ngjyra", "ngjyre"]);
const SIZE_OPTION_NAMES = new Set([
  "size",
  "madhesia",
  "madhesi",
  "numri",
  "numer",
  "nr",
]);

/**
 * Splits one "Name: Value" option segment into a renderable descriptor. The
 * value is always preserved verbatim; only the dimension name is classified so
 * the caller can label it in the user's language.
 */
export function classifyOptionSegment(segment) {
  const raw = String(segment || "").trim();
  if (!raw) {
    return null;
  }

  const colonIndex = raw.indexOf(":");
  if (colonIndex === -1) {
    return { kind: "other", name: null, value: raw };
  }

  const name = raw.slice(0, colonIndex).trim();
  const value = raw.slice(colonIndex + 1).trim();
  if (!value) {
    return { kind: "other", name: null, value: name };
  }

  const key = normalizeOptionName(name);
  if (COLOR_OPTION_NAMES.has(key)) {
    return { kind: "color", name, value };
  }
  if (SIZE_OPTION_NAMES.has(key)) {
    return { kind: "size", name, value };
  }

  return { kind: "other", name, value };
}

export function buildSaleItemDisplayLabel({
  productTitle,
  variantTitle,
  optionSummary,
}) {
  const title = normalizeDisplayToken(productTitle);
  const optionValues = String(optionSummary || "")
    .split(" / ")
    .map((segment) => {
      const colonIndex = segment.indexOf(":");
      return colonIndex === -1
        ? segment.trim()
        : segment.slice(colonIndex + 1).trim();
    })
    .filter(Boolean);

  const parts = [];
  if (title) {
    parts.push(title);
  }

  optionValues.forEach((value) => {
    if (value && !parts.includes(value)) {
      parts.push(value);
    }
  });

  if (!parts.length) {
    const collapsed = collapseSlashSeparatedTitle(variantTitle);
    return collapsed || "—";
  }

  return parts.join(" ");
}
